/* verqo — Homepage "light site, dark punctuation" preview · June 2026
   Same cream base as the live homepage, but the emotional moments are rendered
   in the pitch-deck ink: dark hero, dark banking/trust band, dark full-bleed CTA,
   dark footer. PREVIEW ONLY at /preview/home-punch — "/" is untouched. */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, Banknote, ShieldCheck, Truck, Wallet,
  Sparkles, Store, CreditCard, Radar, FileText, Zap, Building2, Layers,
  Shirt, Coffee, PawPrint, HeartPulse, Home,
} from "lucide-react";
import { PreviewNavHeader } from "../../components/marketing/PreviewNavHeader";
import { PreviewFooter } from "../../components/marketing/PreviewFooter";
import { Reveal } from "../../components/marketing/SubPageBlocks";
import PageSEO from "../../components/marketing/PageSEO";

const PAPER = "#f7f6f2";
const INK = "#0b1914";
const LIME = "#dcff3a";

const Hi = ({ children }) => (
  <span className="relative inline-block px-2 -rotate-1 rounded-sm" style={{ background: LIME, color: INK }}>{children}</span>
);

function PanShot({ src, alt, ratio = "16 / 10", duration = 18, zoom = 1, fill = false }) {
  return (
    <div className={`relative overflow-hidden ${fill ? "h-full w-full" : ""}`} style={fill ? undefined : { aspectRatio: ratio }}>
      <div className="absolute inset-0 hp-ken" style={{ animationDuration: `${duration}s` }}>
        <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover object-top"
             style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }} />
      </div>
    </div>
  );
}

function FrameShot({ src, url, alt, ratio = "16 / 10", duration = 22 }) {
  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-white shadow-[0_28px_80px_-24px_rgba(15,23,42,0.24)]">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
        <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
        {url && <div className="ml-3 flex-1 rounded-md bg-white border border-slate-200 px-2.5 py-0.5 text-[10.5px] text-slate-500 font-mono truncate">{url}</div>}
      </div>
      <PanShot src={src} alt={alt} ratio={ratio} duration={duration} />
    </div>
  );
}

/* Floating chip — dark variant for the ink hero */
function DarkChip({ icon: Icon, title, sub, className, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute hidden lg:flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 backdrop-blur ${className}`}
      style={{ background: "rgba(10,31,28,0.92)", borderColor: "rgba(220,255,58,0.22)", boxShadow: "0 18px 50px -16px rgba(0,0,0,0.6)" }}
    >
      <div className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0" style={{ background: "rgba(220,255,58,0.14)" }}>
        <Icon className="w-4 h-4" strokeWidth={2} style={{ color: LIME }} />
      </div>
      <div>
        <div className="text-[12px] font-semibold leading-tight text-white">{title}</div>
        <div className="text-[10.5px] leading-tight mt-0.5 text-white/55">{sub}</div>
      </div>
      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: LIME }} />
    </motion.div>
  );
}

function BentoCard({ icon: Icon, title, body, img, to, big = false }) {
  return (
    <Link to={to}
      className={`group relative flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-lime-400 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.22)] transition-all ${big ? "lg:col-span-2" : ""}`}
      data-testid={`hp-bento-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="p-6 md:p-7 pb-0">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-lime-100 grid place-items-center">
            <Icon className="w-4 h-4 text-lime-700" strokeWidth={1.8} />
          </div>
          <h3 className="text-[17px] font-semibold tracking-tight text-slate-900">{title}</h3>
        </div>
        <p className="text-[13px] leading-relaxed text-slate-600 max-w-md">{body}</p>
      </div>
      <div className="mt-5 px-6 md:px-7 flex-1">
        <div className="rounded-t-lg border border-b-0 border-slate-200 overflow-hidden shadow-sm h-full min-h-[210px]">
          <PanShot fill src={img} alt={title} zoom={big ? 1.12 : 1.45} duration={16} />
        </div>
      </div>
    </Link>
  );
}

function DeepDive({ eyebrow, title, body, bullets, img, url, to, flip = false, icon: Icon }) {
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <Reveal>
        <div>
          <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.26em] text-lime-700 font-semibold">
            <Icon className="w-3.5 h-3.5" /> {eyebrow}
          </div>
          <h3 className="mt-4 text-[28px] md:text-[38px] font-semibold tracking-tight leading-[1.05] text-slate-900">{title}</h3>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-600 max-w-lg">{body}</p>
          <div className="mt-6 space-y-2.5">
            {bullets.map((b) => (
              <div key={b} className="flex items-start gap-2.5 text-[13.5px] text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 flex-shrink-0" /> {b}
              </div>
            ))}
          </div>
          <Link to={to} className="mt-7 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-900 hover:text-lime-700 transition-colors group">
            Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </Reveal>
      <Reveal delay={0.12} y={30}>
        <FrameShot src={img} url={url} alt={title} duration={22} />
      </Reveal>
    </div>
  );
}

const CATEGORIES = [
  { name: "Beauty", icon: Sparkles },
  { name: "Fashion & Apparel", icon: Shirt },
  { name: "Food & Beverage", icon: Coffee },
  { name: "Pet", icon: PawPrint },
  { name: "Health & Wellness", icon: HeartPulse },
  { name: "Household CPG", icon: Home },
];

const FLOW = [
  { i: Layers, t: "Order" }, { i: Truck, t: "Shipment" }, { i: ShieldCheck, t: "Insurance" },
  { i: Banknote, t: "Financing" }, { i: Wallet, t: "Payment" },
];

export default function HomePunchPreview() {
  return (
    <div className="home-punch min-h-screen text-slate-900" style={{ ["--brand-fg"]: "#0c1612", ["--brand-accent"]: LIME, background: PAPER }}>
      <style>{`
        /* dark chrome so the header sits on the ink hero */
        .home-punch header { background: rgba(11,25,20,0.88) !important; border-bottom-color: rgba(255,255,255,0.08) !important; }
        .home-punch header a, .home-punch header button, .home-punch header span { color: #f7f6f3 !important; }
        .home-punch header a[href="/waitlist?as=seller"], .home-punch header .bg-\\[\\#0c1612\\] { background: ${LIME} !important; color: ${INK} !important; }
        .home-punch header .bg-\\[\\#0c1612\\] span { color: ${INK} !important; }
        /* dark footer */
        .home-punch footer { background: ${INK}; border-top-color: rgba(255,255,255,0.08) !important; margin-top: 0 !important; }
        .home-punch footer a, .home-punch footer div, .home-punch footer p, .home-punch footer span { color: rgba(247,246,243,0.62) !important; }
        .home-punch footer a:hover { color: #ffffff !important; }
        .home-punch footer .border-slate-100 { border-color: rgba(255,255,255,0.08) !important; }
        @keyframes hpKen { 0%,100%{transform:scale(1) translateY(0);} 50%{transform:scale(1.09) translateY(-2.5%);} }
        .hp-ken { animation-name: hpKen; animation-timing-function: ease-in-out; animation-iteration-count: infinite; transform-origin: top center; will-change: transform; }
        @keyframes hpDrift { from { transform: translate3d(0,0,0) scale(1); } to { transform: translate3d(-40px,30px,0) scale(1.12); } }
        .hp-glow { animation: hpDrift 16s ease-in-out infinite alternate; }
      `}</style>
      <PageSEO title="verqo · The modern operating system for wholesale" description="Everything wholesale needs to sell, get paid, and ship — orders, payments, credit terms, shipping, and insurance in one platform." path="/" />
      <PreviewNavHeader modeOverride="seller" />

      {/* ═══ 1 · DARK HERO (punctuation) ═══ */}
      <section className="relative overflow-hidden px-6 pt-20 md:pt-28 pb-16 md:pb-24" style={{ background: INK }} data-testid="hp-hero">
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
             style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
        <div className="hp-glow absolute -top-40 -right-32 w-[620px] h-[620px] pointer-events-none"
             style={{ background: "radial-gradient(closest-side, rgba(220,255,58,0.20), transparent 70%)", filter: "blur(20px)" }} />
        <div className="relative max-w-[820px] mx-auto text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-semibold tracking-wide"
                 style={{ border: `1px solid rgba(220,255,58,0.35)`, background: "rgba(220,255,58,0.10)", color: LIME }} data-testid="hp-hero-kicker">
              <Sparkles className="w-3 h-3" /> Banking built in*
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 text-[38px] sm:text-[50px] md:text-[62px] leading-[1.03] tracking-tight font-semibold text-white" data-testid="hp-hero-headline">
              The modern operating<br className="hidden md:block" /> system for <Hi>wholesale</Hi>.
            </h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 mx-auto max-w-xl text-[16px] md:text-[17px] leading-relaxed text-white/60" data-testid="hp-hero-sub">
              Everything wholesale needs to <strong className="text-white font-semibold">sell, get paid, and ship</strong> —
              orders, payments, credit terms, shipping, and insurance in one platform.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href="/waitlist?as=seller" data-testid="hp-cta-primary"
                 className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold transition-transform hover:-translate-y-0.5"
                 style={{ background: LIME, color: INK }}>
                Get paid today <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link to="/pricing" data-testid="hp-cta-secondary"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg border border-white/25 text-white text-sm font-medium hover:bg-white/10 transition-colors">
                See pricing
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.36}>
            <div className="mt-6 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-[12px] text-white/45">
              {["Same-day cash on every invoice", "Zero default risk — Allianz insured", "First invoice live in 48 hours"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" style={{ color: LIME }} /> {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* seam — screenshot straddles the dark→light boundary */}
      <div className="relative px-6" style={{ background: `linear-gradient(180deg, ${INK} 0%, ${INK} 38%, ${PAPER} 38%, ${PAPER} 100%)` }}>
        <div className="relative max-w-[1240px] mx-auto" data-testid="hp-hero-screenshot">
          <DarkChip icon={Banknote} title="Invoice funded · Day 1" sub="$48,200 wired to wallet" className="-left-5 xl:-left-9 top-24 z-10" delay={0.5} />
          <DarkChip icon={CreditCard} title="Payment matched" sub="PO #4821 · auto-reconciled" className="-right-5 xl:-right-9 top-16 z-10" delay={0.65} />
          <DarkChip icon={ShieldCheck} title="Insured · Allianz" sub="100% of invoice covered" className="-left-5 xl:-left-9 bottom-24 z-10" delay={0.8} />
          <DarkChip icon={Truck} title="Shipment in transit" sub="LA → NYC · ETA Thu" className="-right-5 xl:-right-9 bottom-16 z-10" delay={0.95} />
          <Reveal delay={0.2} y={40}>
            <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_70px_160px_-40px_rgba(0,0,0,0.7)] bg-white">
              <img src="/preview-screens/seller/dashboard-hero.jpeg" alt="Verqo seller dashboard" className="block w-full h-auto" />
            </div>
          </Reveal>
        </div>
      </div>

      {/* ═══ 2 · LIGHT: categories ═══ */}
      <section className="bg-white/50 border-y border-slate-900/[0.06] mt-16 md:mt-20" data-testid="hp-categories">
        <div className="max-w-[1140px] mx-auto px-6 py-10 md:py-12 text-center">
          <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">Built for these categories</div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5 md:gap-3">
            {CATEGORIES.map((c) => (
              <div key={c.name} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 bg-white hover:border-lime-400 hover:-translate-y-0.5 transition-all">
                <c.icon className="w-3.5 h-3.5 text-slate-600" strokeWidth={1.8} />
                <span className="text-[12.5px] font-medium text-slate-700">{c.name}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[13px] text-slate-500">
            For brands, wholesalers &amp; distributors · <span className="font-semibold text-slate-700">$1M–$200M</span> in revenue.
          </p>
        </div>
      </section>

      {/* ═══ 3 · LIGHT: bento ═══ */}
      <section className="max-w-[1140px] mx-auto px-6 pt-20 md:pt-28 pb-8" data-testid="hp-bento">
        <Reveal>
          <div className="max-w-2xl">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">One source of truth</div>
            <h2 className="mt-4 text-[30px] md:text-[44px] font-semibold tracking-tight leading-[1.04] text-slate-900">
              Every transaction, reconciled across everything.
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
              Sales, payments, shipping, and insurance all run through the same ledger —
              so nothing is ever re-keyed, exported, or matched by hand again.
            </p>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <BentoCard big icon={Banknote} title="Get paid on Day 1" body="The moment a PO is approved, the invoice amount is wired to your wallet. Net-30/60/90 becomes your buyer's problem, not yours." img="/preview-screens/seller/invoices.png" to="/product/payments" />
          <BentoCard icon={Wallet} title="One wallet, every dollar" body="Payouts, postage, fees, and credit — one balance, one statement, zero reconciliation." img="/preview-screens/seller/wallet.png" to="/product/payments" />
          <BentoCard icon={Truck} title="Ship from the same screen" body="Parcel and freight, rate-shopped across 75+ carriers, paid from the same wallet." img="/preview-screens/buyer-shipping/main.jpeg" to="/product/shipping" />
          <BentoCard big icon={ShieldCheck} title="Insurance on every order" body="Every funded invoice is Allianz-insured automatically. A default never touches your cash — claims are filed and paid inside the platform." img="/preview-screens/seller/insurance.png" to="/product/insurance" />
        </div>
      </section>

      {/* ═══ 4 · LIGHT: deep dives ═══ */}
      <section className="max-w-[1140px] mx-auto px-6 pt-16 md:pt-24 pb-20 md:pb-28 space-y-24 md:space-y-32" data-testid="hp-deepdives">
        <DeepDive icon={Store} eyebrow="Sell"
          title="Your catalog, storefront, and RFQs — live in a day."
          body="Publish a wholesale storefront, broadcast your catalog to 500+ credentialed buyers, and answer RFQs without a single spreadsheet."
          bullets={["B2B storefront with buyer-specific pricing", "Catalog import from Shopify / CSV in minutes", "RFQ inbox with one-click quoting"]}
          img="/preview-screens/portal-catalog/catalog-grid.jpeg" url="app.verqo.com/seller/catalog" to="/product/catalog" />
        <DeepDive flip icon={Radar} eyebrow="Get paid"
          title="Credit decisions in 60 seconds. Cash the same day."
          body="Verqo underwrites your buyers in real time, extends them Net-30/60/90, and pays you the full invoice on Day 1 — insured end to end."
          bullets={["Live buyer risk scores across the network", "Automated terms — no paper trade references", "0-day DSO on every funded invoice"]}
          img="/preview-screens/seller/credit.png" url="app.verqo.com/seller/credit" to="/product/credit-terms" />
      </section>

      {/* ═══ 5 · DARK: banking at the core (punctuation) ═══ */}
      <section className="relative overflow-hidden px-6 py-20 md:py-28" style={{ background: INK }} data-testid="hp-banking">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
             style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
        <div className="relative max-w-[1140px] mx-auto">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: LIME }}>Banking at the core</div>
              <h2 className="mt-4 text-[30px] md:text-[46px] font-semibold tracking-tight leading-[1.03] text-white">
                Every dollar lands <span style={{ color: LIME }}>already matched.</span>
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-white/55">
                Everywhere else, the bank sits outside the system and your team matches it by hand.
                On verqo the bank <em className="not-italic text-white">is</em> the system.
              </p>
            </div>
          </Reveal>

          <div className="mt-12 space-y-5">
            {/* lane A — everyone else */}
            <Reveal>
              <div className="rounded-2xl border border-white/10 p-6 md:p-7">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/35 font-semibold">Everyone else · bank sits outside</div>
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                  {FLOW.map((f) => (
                    <div key={f.t} className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-white/[0.04] text-[13px] text-white/55">
                      <f.i className="w-4 h-4" strokeWidth={1.8} /> {f.t}
                    </div>
                  ))}
                  <span className="px-3 text-[18px] text-rose-300/80">?</span>
                  <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/20 text-[13px] font-semibold text-white/50">
                    Bank statement
                  </div>
                </div>
                <div className="mt-4 text-[13px] text-rose-300/80">Matched by hand · spreadsheets · weeks late</div>
              </div>
            </Reveal>
            {/* lane B — verqo */}
            <Reveal delay={0.12}>
              <div className="rounded-2xl p-6 md:p-7" style={{ background: "rgba(220,255,58,0.07)", border: "1px solid rgba(220,255,58,0.28)" }}>
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold" style={{ color: LIME }}>verqo · bank is the core</div>
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                  {FLOW.map((f) => (
                    <div key={f.t} className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-[13px] text-white"
                         style={{ background: "rgba(220,255,58,0.10)", border: "1px solid rgba(220,255,58,0.22)" }}>
                      <f.i className="w-4 h-4" strokeWidth={1.8} style={{ color: LIME }} /> {f.t}
                    </div>
                  ))}
                  <span className="px-3 text-[18px]" style={{ color: LIME }}>→</span>
                  <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold" style={{ background: LIME, color: INK }}>
                    <Building2 className="w-4 h-4" /> verqo ledger
                  </div>
                </div>
                <div className="mt-4 text-[13px]" style={{ color: LIME }}>Auto-reconciled to the order the moment money moves</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ 6 · LIGHT: how it works + quote ═══ */}
      <section className="max-w-[1140px] mx-auto px-6 pt-20 md:pt-28 pb-10" data-testid="hp-how">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">How it works</div>
            <h2 className="mt-4 text-[28px] md:text-[40px] font-semibold tracking-tight leading-[1.05] text-slate-900">First funded invoice in 48 hours.</h2>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            [Zap, "01 · Connect", "Import your catalog and buyers. Verqo credentials every buyer against the network in minutes."],
            [FileText, "02 · Sell on terms", "Buyers order with Net-30/60/90 approved in under 60 seconds — you approve the PO."],
            [Banknote, "03 · Get paid Day 1", "The full invoice hits your wallet same-day, Allianz-insured. Ship from the same screen."],
          ].map(([Icon, h, b], i) => (
            <Reveal key={h} delay={i * 0.08}>
              <div className="rounded-2xl border border-slate-200 bg-white p-7 h-full hover:border-lime-400 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-lime-100 grid place-items-center mb-5"><Icon className="w-4 h-4 text-lime-700" strokeWidth={1.8} /></div>
                <div className="text-[15px] font-semibold text-slate-900">{h}</div>
                <p className="mt-2 text-[13px] leading-relaxed text-slate-600">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[820px] mx-auto px-6 pt-10 md:pt-16 pb-20 md:pb-28 text-center" data-testid="hp-quote">
        <Reveal>
          <div className="text-lime-500 text-[44px] leading-none font-serif">“</div>
          <blockquote className="text-[20px] md:text-[26px] font-medium tracking-tight leading-snug text-slate-900">
            We replaced five systems and 60 days of waiting with one login and same-day cash.
            Our books reconcile themselves now.
          </blockquote>
          <div className="mt-5 text-[13px] text-slate-500">Operations lead · mid-market beverage distributor</div>
        </Reveal>
      </section>

      {/* ═══ 7 · DARK: full-bleed CTA (punctuation) ═══ */}
      <section className="relative overflow-hidden px-6 py-20 md:py-28 text-center" style={{ background: INK }} data-testid="hp-cta">
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
             style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="hp-glow absolute -bottom-40 -left-32 w-[560px] h-[560px] pointer-events-none"
             style={{ background: "radial-gradient(closest-side, rgba(220,255,58,0.18), transparent 70%)", filter: "blur(20px)" }} />
        <div className="relative max-w-[900px] mx-auto">
          <h2 className="text-[32px] md:text-[52px] font-semibold tracking-tight leading-[1.03] text-white">
            Ready to run wholesale <span style={{ color: LIME }}>on one system?</span>
          </h2>
          <p className="mt-5 text-[15px] md:text-[16px] text-white/55 max-w-xl mx-auto">
            Sell, get paid, and ship from one platform — banking built in, first invoice live in 48 hours.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <a href="/waitlist?as=seller" data-testid="hp-cta-banner-btn"
               className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold transition-transform hover:-translate-y-0.5"
               style={{ background: LIME, color: INK }}>
              Get paid today <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg border border-white/25 text-white text-sm font-medium hover:bg-white/10 transition-colors">
              See pricing
            </Link>
          </div>
          <p className="mt-10 text-[10.5px] leading-relaxed text-white/30 max-w-2xl mx-auto" data-testid="hp-fdic-note">
            *Verqo is a financial technology company, not a bank. Banking services are provided by our partner banks, Members FDIC.
          </p>
        </div>
      </section>

      {/* ═══ 8 · DARK footer ═══ */}
      <PreviewFooter modeOverride="seller" />
    </div>
  );
}
